"use client";
import React, { useState, useEffect, useRef, Suspense } from 'react';
import {
    ArrowLeft, MapPin, Star, Heart, ChevronDown, ChevronUp,
    Search as SearchIcon, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
    List, X, Minus, Phone, MessageCircle, LayoutGrid, Grid3x3, Columns
} from 'lucide-react';
import { useSearchParams, useRouter, useParams } from "next/navigation";
import { useSelector } from 'react-redux';
import api, { API_BASE_URL } from '@/config/api';
import { showSuccessNotification, showErrorNotification } from '@/utils/notificationService';
import LoginModal from '@/components/LoginModal/LoginModal';
import MessageModal from '@/components/MessageModal/MessageModal';
import Loader from '../../../../../components/Loader/page';
import Lottie from 'lottie-react';
import userSearchingAnimation from "@/assets/lottie/user-searching.json";

const FreelancersPage = () => {
    const searchParams = useSearchParams();
    const router = useRouter();
    const params = useParams();

    // Get serviceId from route params (this page lives at /servicelist/[serviceId]/freelancers)
    const serviceId = params?.serviceId;
    const serviceNameFromUrl = searchParams.get('serviceName');

    // Auth state from Redux
    const { isAuthenticated, user, is_active, userType } = useSelector((state) => state.auth);
    const accountDeactivated = is_active === "0" || is_active === 0 || user?.is_active === "0" || user?.is_active === 0;
    const isCustomer = (userType || user?.user_type || user?.userType)?.toLowerCase() === 'customer';
    const isOwnProfile = (freelancer) => {
        if (!isAuthenticated || !user) return false;
        const currentUserId = user.id || user.fp_u_id || user.u_id;
        const freelancerId = freelancer.id || freelancer.fp_id || freelancer.fp_u_id;
        return currentUserId && freelancerId && String(currentUserId) === String(freelancerId);
    };

    // Modal states
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showContactSuccessModal, setShowContactSuccessModal] = useState(false);
    const [selectedFreelancer, setSelectedFreelancer] = useState(null);
    const [requestingContact, setRequestingContact] = useState({});

    const [showFilterModal, setShowFilterModal] = useState(false);
    const isFirstRender = useRef(true);
    const initializedRef = useRef(false);
    const stateRestoredRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [cardsPerPage, setCardsPerPage] = useState(6);
    const [viewColumns, setViewColumns] = useState(2);
    const [salaryRange, setSalaryRange] = useState({ min: "", max: "" });
    const [experience, setExperience] = useState("");
    const [remoteType, setRemoteType] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedService, setSelectedService] = useState("");
    const [statesList, setStatesList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [subcategoriesList, setSubcategoriesList] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
    const [stateSearchTerm, setStateSearchTerm] = useState("");
    const [citySearchTerm, setCitySearchTerm] = useState("");
    const [categorySearchTerm, setCategorySearchTerm] = useState("");
    const [serviceSearchTerm, setServiceSearchTerm] = useState("");
    const stateDropdownRef = useRef(null);
    const cityDropdownRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    const serviceDropdownRef = useRef(null);
    const categoryRef = useRef(null);
    const locationRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [freelancers, setFreelancers] = useState([]);
    const [filteredFreelancers, setFilteredFreelancers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [allServices, setAllServices] = useState([]);
    const [searchInfo, setSearchInfo] = useState({ keyword: "", category: "" });
    const [categoryFromResponse, setCategoryFromResponse] = useState(null);
    const [serviceIdFromResponse, setServiceIdFromResponse] = useState(null);
    const [allResults, setAllResults] = useState([]);
    const [results, setResults] = useState([]);
    const [sortBy, setSortBy] = useState("Default");
    const [experienceLevel, setExperienceLevel] = useState({ entry: false, intermediate: false, expert: false });
    const [favorites, setFavorites] = useState(new Set());

    const primaryColor = '#3a599c';
    const primaryHover = '#2d477a';

    // Init: restore state from sessionStorage if available
    useEffect(() => {
        if (!initializedRef.current) {
            initializedRef.current = true;
            const savedState = sessionStorage.getItem('serviceFreelancersPageState');
            if (savedState) {
                try {
                    const parsedState = JSON.parse(savedState);
                    if (parsedState.searchInfo) setSearchInfo(parsedState.searchInfo);
                    if (parsedState.selectedCategory) setSelectedCategory(parsedState.selectedCategory);
                    if (parsedState.selectedService) setSelectedService(parsedState.selectedService);
                    if (parsedState.selectedState) setSelectedState(parsedState.selectedState);
                    if (parsedState.selectedCity) setSelectedCity(parsedState.selectedCity);
                    if (parsedState.salaryRange) setSalaryRange(parsedState.salaryRange);
                    if (parsedState.experience) setExperience(parsedState.experience);
                    if (parsedState.experienceLevel) setExperienceLevel(parsedState.experienceLevel);
                    if (parsedState.remoteType) setRemoteType(parsedState.remoteType);
                    if (parsedState.sortBy) setSortBy(parsedState.sortBy);
                    if (parsedState.viewColumns) setViewColumns(parsedState.viewColumns);
                    if (parsedState.currentPage) setCurrentPage(parsedState.currentPage);
                    
                    if (parsedState.freelancers && parsedState.freelancers.length > 0) {
                        setFreelancers(parsedState.freelancers);
                        setFilteredFreelancers(parsedState.filteredFreelancers || parsedState.freelancers);
                        setAllResults(parsedState.allResults || parsedState.freelancers);
                        setResults(parsedState.results || parsedState.freelancers);
                        stateRestoredRef.current = true;
                    }
                } catch (e) {
                    console.error('Failed to parse saved state:', e);
                }
                sessionStorage.removeItem('serviceFreelancersPageState');
            }
        }
    }, []);

    // Update cards per page when view columns change
    useEffect(() => {
        if (viewColumns === 3) setCardsPerPage(9);
        else if (viewColumns === 4) setCardsPerPage(16);
        else setCardsPerPage(6);

        if (!isFirstRender.current) {
            setCurrentPage(1);
        }
    }, [viewColumns]);

    // Mark first render as complete
    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    // Mobile detection
    useEffect(() => {
        function handleResize() { setIsMobile(window.innerWidth < 640); }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleFilterModal = () => {
        setShowFilterModal(!showFilterModal);
    };

    const handleSalaryChange = (e, type) => {
        const inputValue = e.target.value;
        // Allow empty string for user to type, convert to number only when not empty
        if (inputValue === "") {
            if (type === "min") setSalaryRange((prev) => ({ ...prev, min: "" }));
            else setSalaryRange((prev) => ({ ...prev, max: "" }));
        } else {
            const numValue = parseInt(inputValue, 10);
            if (!isNaN(numValue)) {
                if (type === "min") setSalaryRange((prev) => ({ ...prev, min: numValue }));
                else setSalaryRange((prev) => ({ ...prev, max: numValue }));
            }
        }
    };

    /* ---------- Helpers (same as freelancers-list) ---------- */
    const formatPrice = (price) => {
        const p = Number(price) || 0;
        return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(p);
    };
    const capitalizeEachWord = (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const getProfileImage = (imagePath, cacheKey) => {
        if (!imagePath || imagePath === "0") return "https://ui-avatars.com/api/?name=User&background=3e5a9a&color=fff";
        const cleanPath = imagePath.split("--")[0];
        const baseUrl = cleanPath.startsWith("http") || cleanPath.startsWith("https") ? cleanPath : `${API_BASE_URL.replace('/api', '')}/${cleanPath}`;
        const key = cacheKey ? String(cacheKey) : "";
        const sep = baseUrl.includes("?") ? "&" : "?";
        return key ? `${baseUrl}${sep}v=${encodeURIComponent(key)}` : baseUrl;
    };
    const getLocationString = (u) => {
        const parts = [];
        if (u.city_name) parts.push(u.city_name);
        if (u.state_name) parts.push(u.state_name);
        if (u.fp_co_id === 101 || String(u.fp_co_id) === "101") parts.push("India");
        return parts.length > 0 ? parts.join(", ") : "Location not specified";
    };
    const isVerified = (u) => u && (u.fp_is_verify === "1" || u.fp_is_verify === 1 || u.fp_is_verify === true);
    const getLanguages = (u) => {
        if (!u?.fp_lang || u.fp_lang === "0") return [];
        return u.fp_lang.split(",").map((s) => s.trim()).filter(Boolean);
    };
    const toggleFavorite = (userId) => {
        setFavorites((prev) => { const next = new Set(prev); if (next.has(userId)) next.delete(userId); else next.add(userId); return next; });
    };
    const toggleExperienceLevel = (level) => setExperienceLevel((prev) => ({ ...prev, [level]: !prev[level] }));
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    /* ---------- Message / Contact Handlers ---------- */
    const handleSendMessage = (freelancer) => {
        if (accountDeactivated) { alert('Your account is deactivated.'); return; }
        if (isOwnProfile(freelancer)) { alert('You cannot send a message to yourself.'); return; }
        if (!isAuthenticated) { setSelectedFreelancer(freelancer); setShowLoginModal(true); }
        else { setSelectedFreelancer(freelancer); setShowMessageModal(true); }
    };
    const handleLoginSuccess = () => { setShowLoginModal(false); setShowMessageModal(true); };
    const handleContactSuccessClose = () => {
        setShowContactSuccessModal(false);
        setTimeout(() => {
            if (isAuthenticated && user) {
                const currentUserType = (userType || user?.user_type || user?.userType)?.toLowerCase();
                if (currentUserType === 'freelancer') router.push('/freelancer-dashboard/message');
                else if (currentUserType === 'customer' || currentUserType === 'client') router.push('/customer-dashboard/message');
                else router.push('/');
            } else router.push('/');
        }, 100);
    };
    const handleRequestContactDetails = async (freelancerId, freelancerName) => {
        if (accountDeactivated) { alert('Your account is deactivated.'); return; }
        if (!freelancerId) { showErrorNotification("Unable to identify the freelancer."); return; }
        if (!isAuthenticated) { setSelectedFreelancer({ id: freelancerId, name: freelancerName }); setShowLoginModal(true); return; }
        const currentUserId = user?.id || user?.fp_u_id || user?.u_id;
        if (currentUserId && String(currentUserId) === String(freelancerId)) { alert('You cannot request your own contact details.'); return; }
        if (!isCustomer) { alert('Only customers can request contact details.'); return; }
        setRequestingContact(prev => ({ ...prev, [freelancerId]: true }));
        try {
            const response = await api.post('/share-contact/send', { sc_message_id: 1, sc_request_receive_id: freelancerId, sc_request_send_message: 'Hi, Please share your contact details.' });
            if (response.data.status === 'success') setShowContactSuccessModal(true);
            else showErrorNotification(response.data.message || "Failed to send contact request.");
        } catch (error) {
            showErrorNotification(error.response?.data?.message || "Failed to send contact request.");
        } finally {
            setRequestingContact(prev => ({ ...prev, [freelancerId]: false }));
        }
    };

    // Fetch states, cities, categories, and services list
    useEffect(() => {
        const fetchLocationData = async () => {
            try {
                const [locationResponse, siteDataResponse] = await Promise.all([
                    api.get('/getStatelist'),
                    api.get('/getSiteData')
                ]);
                const locationData = locationResponse.data || {};
                setStatesList(Array.isArray(locationData.state) ? locationData.state : []);
                setCitiesList(Array.isArray(locationData.city) ? locationData.city : []);

                // Get categories and services from site data
                const siteData = siteDataResponse?.data || {};
                const scList = Array.isArray(siteData.sc_list) ? siteData.sc_list : [];
                const seList = Array.isArray(siteData.se_list) ? siteData.se_list : [];

                // Extract unique categories from subcategories and store subcategories
                // Filter out categories/subcategories that contain "other" (case-insensitive)
                const filteredSubcategories = scList.filter((sc) => {
                    const name = (sc.sc_name || sc.name || "").toLowerCase();
                    return !name.includes("other");
                });

                const uniqueCategories = [];
                const categoryMap = new Map();
                filteredSubcategories.forEach((sc) => {
                    if (sc?.get_ca_data?.ca_id && !categoryMap.has(sc.get_ca_data.ca_id)) {
                        categoryMap.set(sc.get_ca_data.ca_id, sc.get_ca_data);
                        uniqueCategories.push(sc.get_ca_data);
                    }
                });
                setCategoriesList(uniqueCategories);
                setSubcategoriesList(filteredSubcategories); // Store filtered subcategories (excluding "other")
                setServicesList(seList);
                setAllServices(seList); // Store all services for service lookup
            } catch (err) {
                console.error('Error fetching location/service data:', err);
            }
        };
        fetchLocationData();
    }, []);

    // Filter cities based on selected state
    useEffect(() => {
        if (selectedState) {
            const stateId = String(selectedState);
            const filtered = citiesList.filter(city => String(city.cit_state_id) === stateId);
            setFilteredCities(filtered);
            setSelectedCity(prev => {
                if (!prev) return "";
                const cityExists = filtered.some(c => String(c.cit_id) === String(prev));
                return cityExists ? prev : "";
            });
        } else {
            setFilteredCities([]);
            setSelectedCity("");
        }
    }, [selectedState, citiesList]);

    // Filter services based on selected subcategory (category in UI)
    useEffect(() => {
        if (selectedCategory) {
            const subcategoryId = String(selectedCategory);
            // Filter services that belong to the selected subcategory
            const filtered = servicesList.filter(service =>
                String(service.se_sc_id) === subcategoryId ||
                String(service.sc_id) === subcategoryId ||
                String(service.subcategory_id) === subcategoryId
            );
            setFilteredServices(filtered);
            setSelectedService(prev => {
                if (!prev) return "";
                const svcExists = filtered.some(s => String(s.se_id) === String(prev));
                return svcExists ? prev : "";
            });
        } else {
            setFilteredServices([]);
            setSelectedService("");
        }
    }, [selectedCategory, servicesList]);

    // Init: read search params and add outside click handler
    useEffect(() => {
        const keyword = searchParams.get("search_key") || "";
        const category = searchParams.get("search_category") || "";

        if (!stateRestoredRef.current) {
            setSearchInfo({ keyword, category });

            // map category id -> name if needed (placeholder)
            if (category) {
                setSelectedCategory("All Categories");
            }
        }

        function handleClickOutside(event) {
            // if (categoryRef.current && !categoryRef.current.contains(event.target)) {
            //   setOpenCategory(false);
            // }
            // if (locationRef.current && !locationRef.current.contains(event.target)) {
            //   setOpenLocation(false);
            // }
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
                setStateDropdownOpen(false);
                setStateSearchTerm("");
            }
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
                setCityDropdownOpen(false);
                setCitySearchTerm("");
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setCategoryDropdownOpen(false);
                setCategorySearchTerm("");
            }
            if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
                setServiceDropdownOpen(false);
                setServiceSearchTerm("");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchParams]);

    // Fetch freelancers by service when serviceId and allServices are available
    useEffect(() => {
        const fetchFreelancersByService = async () => {
            if (!serviceId || allServices.length === 0) return;

            setLoading(true);
            setError(null);

            try {
                // Find the service by slug or numeric ID
                let service = allServices.find(s => s.se_slug === serviceId);
                let numericServiceId = null;
                let serviceSlug = '';

                if (service) {
                    serviceSlug = service.se_slug;
                    numericServiceId = service.se_id;
                    setServiceDetails(service);
                } else {
                    // Try by numeric ID
                    numericServiceId = parseInt(serviceId);
                    if (!isNaN(numericServiceId)) {
                        service = allServices.find(s => s.se_id === numericServiceId);
                        if (service) {
                            serviceSlug = service.se_slug;
                            setServiceDetails(service);
                        }
                    }
                }

                // Fallback by service name from URL
                if (!service && serviceNameFromUrl) {
                    service = allServices.find(s => (s.se_name || '').toLowerCase() === serviceNameFromUrl.toLowerCase());
                    if (service) {
                        serviceSlug = service.se_slug;
                        numericServiceId = service.se_id;
                        setServiceDetails(service);
                    }
                }

                console.log('Fetching freelancers for service:', { serviceId, serviceSlug, numericServiceId });

                if (stateRestoredRef.current) {
                    setLoading(false);
                    return;
                }

                if (!serviceSlug) {
                    console.warn('Service slug not found, falling back to search API');
                    // Fallback: use the general search API
                    // This part needs to be handled by calling applyFilters or a similar search function
                    // For now, we'll just set empty and stop loading.
                    setFreelancers([]);
                    setFilteredFreelancers([]);
                    setLoading(false);
                    return;
                }

                // Fetch freelancers for this specific service
                const freelancersResponse = await api.get(`/getServiceUser/${serviceSlug}/${numericServiceId}`);
                console.log('Service freelancers response:', freelancersResponse.data);

                let freelancersList = [];

                if (freelancersResponse.data?.fe_service_list) {
                    if (Array.isArray(freelancersResponse.data.fe_service_list)) {
                        freelancersList = freelancersResponse.data.fe_service_list;
                    } else {
                        freelancersList = [freelancersResponse.data.fe_service_list];
                    }
                } else if (Array.isArray(freelancersResponse.data)) {
                    freelancersList = freelancersResponse.data;
                }

                // Extract category/service IDs from response for filter context
                if (freelancersList.length > 0) {
                    const first = freelancersList[0];
                    if (first.fp_sc_id) setCategoryFromResponse(first.fp_sc_id);
                    else if (first.se_sc_id) setCategoryFromResponse(first.se_sc_id);
                    if (first.fp_se_id) setServiceIdFromResponse(first.fp_se_id);
                    else if (first.se_id) setServiceIdFromResponse(first.se_id);
                }

                // The service API returns data in a different format than the search API.
                // We need to normalize it so the card rendering works properly.
                // The search API returns objects with fields like: fp_display_name, fp_img, fp_amt_hour, etc.
                // The service API returns similar fields, so we can use them directly.
                const processedData = processFreelancerData(freelancersList, service);
                setFreelancers(processedData);
                setFilteredFreelancers(processedData);
                setAllResults(processedData); // Assuming allResults should also be updated
                setResults(processedData); // Assuming results should also be updated

                // Sync page from URL
                const pageFromUrl = parseInt(searchParams.get("page")) || 1;
                setCurrentPage(pageFromUrl);
            } catch (err) {
                console.error('Error fetching service freelancers:', err);
                if (err.response?.status === 404) {
                    // No freelancers for this service - show empty state, not error
                    setFreelancers([]);
                    setFilteredFreelancers([]);
                    setAllResults([]);
                    setResults([]);
                } else {
                    setError('Failed to load freelancers for this service. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchFreelancersByService();
    }, [serviceId, allServices]);


    const getServiceSlug = (serviceId) => {
        const service = allServices.find(s => s.se_id === parseInt(serviceId));
        return service ? service.se_slug : '';
    };
    const displayServiceName = serviceNameFromUrl || serviceDetails?.se_name || 'Freelancers';


    useEffect(() => {
        const handleScroll = () => {
            const scrollToTopBtn = document.querySelector('.scroll-to-top-btn');
            if (window.scrollY > 300) {
                scrollToTopBtn?.classList.add('opacity-100');
            } else {
                scrollToTopBtn?.classList.remove('opacity-100');
            }

            if (window.scrollY > 10) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);




    // Dedicated effect for client-side sorting and reactive display
    useEffect(() => {
        if (!allResults || allResults.length === 0) {
            setFilteredFreelancers([]);
            setFreelancers([]);
            setResults([]);
            return;
        }

        let sorted = [...allResults];
        
        // Always apply Default sort by profile completion percentage (Platinum > Gold > Silver)
        sorted.sort((a, b) => {
            const pctA = Number(a.profile_percentage) || 0;
            const pctB = Number(b.profile_percentage) || 0;
            return pctB - pctA;
        });

        if (sortBy !== "Default") {
            sorted.sort((a, b) => {
                switch (sortBy) {
                    case "Newest":
                        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                    case "Price: Low to High":
                        return (Number(a.hourlyRate) || 0) - (Number(b.hourlyRate) || 0);
                    case "Price: High to Low":
                        return (Number(b.hourlyRate) || 0) - (Number(a.hourlyRate) || 0);
                    case "Rating":
                        const rA = Number(a.rating) || 0;
                        const rB = Number(b.rating) || 0;
                        return rB - rA;
                    default:
                        return 0;
                }
            });
        }
        setFilteredFreelancers(sorted);
        setFreelancers(sorted);
        setResults(sorted);

        // Reset to first page when sort changes (but not on first load if URL has a page)
        if (!isFirstRender.current) {
            const urlPage = searchParams.get("page");
            if (!urlPage) setCurrentPage(1);
        }
    }, [sortBy, allResults, searchParams]);

    // Pagination logic
    const indexOfLastCard = currentPage * cardsPerPage;
    const indexOfFirstCard = indexOfLastCard - cardsPerPage;
    const currentFreelancers = filteredFreelancers.slice(indexOfFirstCard, indexOfLastCard);
    const totalPages = Math.ceil(filteredFreelancers.length / cardsPerPage);

    const updateUrlWithPage = (pageNumber) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set("page", pageNumber);
        router.push(`?${params.toString()}`, { scroll: false });
    };

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        updateUrlWithPage(pageNumber);
        scrollToTop();
    };

    const nextPage = () => {
        if (currentPage < totalPages) {
            paginate(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            paginate(currentPage - 1);
        }
    };

    const handleViewProfile = (freelancer) => {
        // Save state before navigating
        const appState = {
            searchInfo,
            selectedCategory,
            selectedService,
            selectedState,
            selectedCity,
            salaryRange,
            experience,
            experienceLevel,
            remoteType,
            sortBy,
            viewColumns,
            currentPage,
            freelancers,
            filteredFreelancers,
            allResults,
            results
        };
        sessionStorage.setItem('serviceFreelancersPageState', JSON.stringify(appState));

        // Prefer slug route for public profile
        if (freelancer.slug) {
            router.push(`/freelancer-profile/${encodeURIComponent(freelancer.slug)}`);
            return;
        }
        // Fallback to ID-based route under service context
        router.push(`/servicelist/${serviceId}/freelancers/${freelancer.id}?name=${encodeURIComponent(freelancer.name)}`);
    };

    const getBadge = (percentage) => {
        const p = Number(percentage) || 0;
        if (p >= 80) return { name: "Platinum", color: "bg-gradient-to-r from-gray-100 to-gray-300 text-gray-800 border-gray-400" };
        if (p >= 60) return { name: "Gold", color: "bg-gradient-to-r from-yellow-100 to-yellow-300 text-yellow-800 border-yellow-500" };
        if (p >= 40) return { name: "Silver", color: "bg-gradient-to-r from-slate-100 to-slate-300 text-slate-800 border-slate-400" };
        return null; // Less than 40%
    };

    // Helper function to process freelancer data from API response
    const processFreelancerData = (freelancersList, serviceData = null) => {
        return freelancersList.map((freelancer, index) => {
            // Handle image URL properly
            let imageUrl = "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face";

            if (freelancer.fp_img) {
                // Clean up the image path - remove duplicate filenames
                let cleanImagePath = freelancer.fp_img;
                if (cleanImagePath.includes('--')) {
                    cleanImagePath = cleanImagePath.split('--')[0];
                }
                // Remove leading slash if present
                cleanImagePath = cleanImagePath.replace(/^\//, '');
                imageUrl = `https://hyreback.hyrelancer.com/${cleanImagePath}`;
            }

            // Get service name from freelancer data or serviceData
            const serviceNameForSkills = serviceData?.se_name ||
                freelancer.service_name ||
                freelancer.se_name ||
                '';

            return {
                id: freelancer.fp_id || freelancer.id || index,
                name: freelancer.fp_display_name || freelancer.name || "Unknown",
                slug: freelancer.us_slug || freelancer.fp_slug || freelancer.slug || null,
                location: freelancer.address || freelancer.city_name || freelancer.state_name || "India",
                rating: freelancer.average_rating,
                reviews: freelancer.total_reviews,
                hourlyRate: parseInt(freelancer.fp_amt_hour || freelancer.hourly_rate) || 20,
                projects: freelancer.total_projects,
                skills: serviceNameForSkills ? [serviceNameForSkills] : [],
                services: serviceData?.se_id ? [serviceData.se_id] : (freelancer.se_id ? [freelancer.se_id] : []),
                verified: freelancer.fp_is_verify === "1" || freelancer.fp_is_verify === 1 || freelancer.verified === true,
                image: imageUrl,
                headline: freelancer.fp_headline || freelancer.headline || "",
                description: freelancer.fp_desc || freelancer.description || "",
                experience: freelancer.fp_ex_year || freelancer.experience || "0",
                occupation: freelancer.fp_occupation || freelancer.occupation || "",
                availability: freelancer.fp_available || freelancer.availability || "Available now",
                completingTime: freelancer.fp_completing_time || freelancer.completing_time || "As per requirement",
                amountFor: freelancer.fp_amount_for,
                created_at: freelancer.created_at,
                is_digilocker: freelancer.is_digilocker,
                profile_percentage: freelancer.profile_percentage
            };
        });
    };

    // Filtering logic - Backend API call
    const applyFilters = async () => {
        setLoading(true);
        setError(null);

        try {
            const body = {};

            // Add search keyword if available
            if (searchInfo.keyword) {
                body.search_key = searchInfo.keyword;
            }

            // Add subcategory filter - use service context if available
            // Priority: selectedCategory > categoryFromResponse > searchInfo.category
            if (selectedCategory) {
                body.search_category = selectedCategory;
            } else if (categoryFromResponse) {
                body.search_category = String(categoryFromResponse);
            } else if (searchInfo.category) {
                body.search_category = searchInfo.category;
            }

            // Add service filter - use service context if available
            // Priority: selectedService > serviceIdFromResponse > serviceDetails
            if (selectedService) {
                body.service_val = selectedService;
            } else if (serviceIdFromResponse) {
                body.service_val = String(serviceIdFromResponse);
            } else if (serviceDetails?.se_id) {
                body.service_val = String(serviceDetails.se_id);
            }

            // Add state filter
            if (selectedState) {
                body.state_id = selectedState;
            }

            // Add city filter
            if (selectedCity) {
                body.city_id = selectedCity;
            }

            // Add salary/budget range filter - backend expects min_amt_val and max_amt_val
            // Only send if user has entered values
            if (salaryRange.min !== "" && typeof salaryRange.min === "number") {
                body.min_amt_val = salaryRange.min;
            }
            if (salaryRange.max !== "" && typeof salaryRange.max === "number") {
                body.max_amt_val = salaryRange.max;
            }

            // Add experience filter - backend expects ex_val in text format like "0 to 1 year", "2 to 5 year", etc.
            // Priority: If experience dropdown is selected, use it. Otherwise, use experience level checkboxes.
            if (experience) {
                let exVal = "";
                if (experience === "0-To 1-Year") {
                    exVal = "0 to 1 year";
                } else if (experience === "2-To-5-Year") {
                    exVal = "2 to 5 year";
                } else if (experience === "5-To-10-Year") {
                    exVal = "5 to 10 year";
                } else if (experience === "10-Year") {
                    exVal = "10 year"; // or "more than 10 year" depending on database format
                }
                if (exVal) {
                    body.ex_val = exVal;
                }
            } else {
                // Only use experience level checkboxes if dropdown is not selected
                const experienceRanges = [];
                if (experienceLevel.entry) {
                    experienceRanges.push("0 to 2 year");
                }
                if (experienceLevel.intermediate) {
                    experienceRanges.push("3 to 7 year");
                }
                if (experienceLevel.expert) {
                    experienceRanges.push("8 year"); // or "more than 8 year" depending on database format
                }
                // If experience level is selected but no experience dropdown, use the first range
                if (experienceRanges.length > 0) {
                    body.ex_val = experienceRanges[0];
                }
            }

            // Add remote type filter
            if (remoteType) {
                body.remote_type = remoteType;
            }

            // Debug: Log the request body
            console.log("Filter request body:", body);

            // Call backend API
            const response = await api.post("/search", body);

            const data = response.data;
            const resultsData = data.results || [];

            const processedData = processFreelancerData(resultsData, serviceDetails);
            setAllResults(processedData);
            setCurrentPage(1);
            updateUrlWithPage(1);

            // Close mobile filter modal if open
            if (showFilterModal) {
                setShowFilterModal(false);
            }

        } catch (error) {
            console.error("Filter error:", error);
            console.error("Error details:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });

            if (error.response?.status === 404) {
                setFreelancers([]);
                setFilteredFreelancers([]);
                setError(null);
            } else if (error.message && error.message.includes('Network Error')) {
                setError('Network error. Please check your connection.');
            } else if (error.response?.status >= 500) {
                setError('Server error. Please try again later.');
            } else {
                setError('Failed to apply filters. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="mb-4">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
                        <p className="text-red-500 mb-4">{error}</p>
                        <div className="text-sm text-gray-600 mb-6">
                            <p>Service ID: {serviceId}</p>
                            <p>Service Name: {serviceNameFromUrl || 'Not provided'}</p>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <button
                            className="w-full px-4 py-2 bg-[#3a599c] text-white rounded-lg hover:bg-[#2d477a] transition-colors"
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </button>
                        <button
                            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => router.back()}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Breadcrumb */}
            <section className="breadcrumb relative">
                <div className="relative h-[250px] md:h-[250px] lg:h-[250px]">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                        <div
                            className="w-full h-full bg-cover bg-center"
                            style={{
                                backgroundImage: `url('/images/contact-us_banner.webp')`, // your image path
                            }}
                        >
                            <div className="absolute inset-0 bg-black opacity-50"></div>
                            <div className="relative z-10 flex flex-col items-center justify-center h-full text-white px-4 text-center">
                                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 lg:mb-6 leading-tight">
                                    {serviceDetails?.se_name
                                        ? `${serviceDetails.se_name} Freelancers`
                                        : searchInfo.keyword
                                            ? `Results for "${searchInfo.keyword}"`
                                            : "Find Freelancers"}
                                </h1>
                                <p className="text-sm sm:text-base text-gray-200 max-w-2xl">
                                    Discover top-rated freelancers for your projects. Filter by location, budget, experience, and more.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Foreground content */}

                </div>

            </section>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6 lg:py-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
                {/* Mobile Filter Button */}
                <button
                    onClick={toggleFilterModal}
                    className="lg:hidden fixed bottom-6 right-6 z-40 bg-[#3a599c] text-white p-3 rounded-full shadow-lg flex items-center justify-center"
                    aria-label="Open filters"
                    style={{ backgroundColor: primaryColor }}
                >
                    <List size={24} />
                    <span className="ml-2 text-sm font-medium">Filters</span>
                </button>

                {/* Sidebar - Desktop */}
                <aside className="hidden lg:block w-full lg:w-1/4 bg-white p-6 rounded-xl shadow-sm h-fit sticky top-4 border border-gray-100">
                    <div className="space-y-6">
                        {/* State Filter - Searchable Dropdown */}
                        <div>
                            <h3 className="font-semibold mb-3 text-gray-800">State</h3>
                            <div className="relative" ref={stateDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStateDropdownOpen(!stateDropdownOpen);
                                        setCityDropdownOpen(false);
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all text-left flex items-center justify-between"
                                >
                                    <span className={selectedState ? "text-gray-900" : "text-gray-500"}>
                                        {selectedState
                                            ? statesList.find(s => String(s.s_id) === String(selectedState))?.s_name || "Select State"
                                            : "All States"}
                                    </span>
                                    <ChevronDown className={`text-gray-400 transition-transform ${stateDropdownOpen ? 'transform rotate-180' : ''}`} size={16} />
                                </button>
                                {stateDropdownOpen && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                                            <input
                                                type="text"
                                                placeholder="Search states..."
                                                value={stateSearchTerm}
                                                onChange={(e) => setStateSearchTerm(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedState("");
                                                    setStateDropdownOpen(false);
                                                    setStateSearchTerm("");
                                                }}
                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${!selectedState ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'}`}
                                            >
                                                All States
                                            </button>
                                            {statesList
                                                .filter(state =>
                                                    state.s_name.toLowerCase().includes(stateSearchTerm.toLowerCase())
                                                )
                                                .map((state) => (
                                                    <button
                                                        key={state.s_id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedState(String(state.s_id));
                                                            setStateDropdownOpen(false);
                                                            setStateSearchTerm("");
                                                        }}
                                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${String(selectedState) === String(state.s_id) ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'
                                                            }`}
                                                    >
                                                        {state.s_name}
                                                    </button>
                                                ))}
                                            {statesList.filter(state =>
                                                state.s_name.toLowerCase().includes(stateSearchTerm.toLowerCase())
                                            ).length === 0 && (
                                                    <div className="px-4 py-2 text-sm text-gray-500 text-center">No states found</div>
                                                )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* City Filter - Searchable Dropdown */}
                        <div>
                            <h3 className="font-semibold mb-3 text-gray-800">City</h3>
                            <div className="relative" ref={cityDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (selectedState) {
                                            setCityDropdownOpen(!cityDropdownOpen);
                                            setStateDropdownOpen(false);
                                        }
                                    }}
                                    disabled={!selectedState}
                                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all text-left flex items-center justify-between ${!selectedState ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'text-gray-700'
                                        }`}
                                >
                                    <span className={selectedCity ? "text-gray-900" : "text-gray-500"}>
                                        {!selectedState
                                            ? "Select State First"
                                            : selectedCity
                                                ? filteredCities.find(c => String(c.cit_id) === String(selectedCity))?.cit_name || "Select City"
                                                : "All Cities"}
                                    </span>
                                    <ChevronDown className={`text-gray-400 transition-transform ${cityDropdownOpen ? 'transform rotate-180' : ''}`} size={16} />
                                </button>
                                {cityDropdownOpen && selectedState && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                                            <input
                                                type="text"
                                                placeholder="Search cities..."
                                                value={citySearchTerm}
                                                onChange={(e) => setCitySearchTerm(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedCity("");
                                                    setCityDropdownOpen(false);
                                                    setCitySearchTerm("");
                                                }}
                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${!selectedCity ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'}`}
                                            >
                                                All Cities
                                            </button>
                                            {filteredCities
                                                .filter(city =>
                                                    city.cit_name.toLowerCase().includes(citySearchTerm.toLowerCase())
                                                )
                                                .map((city) => (
                                                    <button
                                                        key={city.cit_id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedCity(String(city.cit_id));
                                                            setCityDropdownOpen(false);
                                                            setCitySearchTerm("");
                                                        }}
                                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${String(selectedCity) === String(city.cit_id) ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'
                                                            }`}
                                                    >
                                                        {city.cit_name}
                                                    </button>
                                                ))}
                                            {filteredCities.filter(city =>
                                                city.cit_name.toLowerCase().includes(citySearchTerm.toLowerCase())
                                            ).length === 0 && (
                                                    <div className="px-4 py-2 text-sm text-gray-500 text-center">No cities found</div>
                                                )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Budget Filter */}
                        <div>
                            <h3 className="font-semibold mb-3 text-gray-800">Filter by Budget</h3>
                            <div className="flex flex-col mt-4 gap-3">
                                <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        value={salaryRange.min === "" ? "" : salaryRange.min}
                                        onChange={(e) => handleSalaryChange(e, "min")}
                                        min="0"
                                        max={typeof salaryRange.max === "number" ? salaryRange.max : undefined}
                                        step="1"
                                        placeholder="0"
                                        className="w-full pl-7 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                    />
                                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-400">min</span>
                                </div>
                                <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        value={salaryRange.max === "" ? "" : salaryRange.max}
                                        onChange={(e) => handleSalaryChange(e, "max")}
                                        min={typeof salaryRange.min === "number" ? salaryRange.min : 0}
                                        step="1"
                                        placeholder="100000"
                                        className="w-full pl-7 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                    />
                                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-400">max</span>
                                </div>
                            </div>
                        </div>

                        {/* Experience */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold mb-3 text-gray-800">Experience</h3>
                            <div className="relative">
                                <select
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 appearance-none bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all"
                                    value={experience}
                                    onChange={(e) => setExperience(e.target.value)}
                                >
                                    <option value="">Select Experience</option>
                                    <option value="0-To 1-Year">0 To 1 Year</option>
                                    <option value="2-To-5-Year">2 To 5 Year</option>
                                    <option value="5-To-10-Year">5 To 10 Year</option>
                                    <option value="10-Year">More Than 10 Year</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                            </div>
                        </div>

                        {/* Remote Work Filter */}
                        <div>
                            <h3 className="font-semibold mb-3 text-gray-800">Work Type</h3>
                            <div className="relative">
                                <select
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 appearance-none bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all"
                                    value={remoteType}
                                    onChange={(e) => setRemoteType(e.target.value)}
                                >
                                    <option value="">All Work Types</option>
                                    <option value="Remote">Remote</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                            </div>
                        </div>

                        {/* Apply Filters Button */}
                        <button
                            onClick={applyFilters}
                            className="w-full py-3 text-white rounded-lg font-medium transition-colors shadow-sm hover:opacity-90"
                            style={{ backgroundColor: primaryColor }}
                        >
                            Apply Filters
                        </button>

                        {/* Clear Filters Button */}
                        <button
                            className="w-full py-2 text-gray-600 border border-gray-300 rounded-lg font-medium transition-colors shadow-sm hover:bg-gray-50"
                            onClick={() => {
                                setSalaryRange({ min: "", max: "" });
                                setExperience("");
                                setSelectedState("");
                                setSelectedCity("");
                                setRemoteType("");
                                setStateDropdownOpen(false);
                                setCityDropdownOpen(false);
                                setStateSearchTerm("");
                                setCitySearchTerm("");
                                setCurrentPage(1);
                                updateUrlWithPage(1);
                            }}
                        >
                            Clear Filters
                        </button>
                    </div>
                </aside>

                {/* Freelancer Cards Grid */}
                <main className="w-full lg:w-3/4">
                    {/* Results Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto self-end sm:self-auto">
                            <div className="hidden lg:flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
                                <button onClick={() => setViewColumns(2)} className={`p-1.5 rounded ${viewColumns === 2 ? 'bg-[#3a599c] text-white' : 'text-gray-500 hover:bg-gray-100'}`} title="2 Columns"><Columns size={16} /></button>
                                <button onClick={() => setViewColumns(3)} className={`p-1.5 rounded ${viewColumns === 3 ? 'bg-[#3a599c] text-white' : 'text-gray-500 hover:bg-gray-100'}`} title="3 Columns"><LayoutGrid size={16} /></button>
                                <button onClick={() => setViewColumns(4)} className={`p-1.5 rounded ${viewColumns === 4 ? 'bg-[#3a599c] text-white' : 'text-gray-500 hover:bg-gray-100'}`} title="4 Columns"><Grid3x3 size={16} /></button>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                                <span className="text-gray-600 text-xs sm:text-sm flex items-center flex-wrap gap-1">
                                    <span className="whitespace-nowrap">Sort by:</span>
                                    <select className="text-gray-800 font-medium border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#3a599c] text-xs sm:text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                        <option>Default</option><option>Newest</option><option>Price: Low to High</option><option>Price: High to Low</option><option>Rating</option>
                                    </select>
                                </span>
                            </div>
                        </div>
                        <div className="text-gray-600 text-xs sm:text-sm w-full sm:w-auto text-left sm:text-right">
                            <span className="block sm:inline">Showing {filteredFreelancers.length === 0 ? 0 : indexOfFirstCard + 1}-{Math.min(indexOfLastCard, filteredFreelancers.length)} of {filteredFreelancers.length}</span>
                            <span className="hidden sm:inline"> results</span>
                        </div>
                    </div>

                    {/* Freelancer Cards */}
                    {filteredFreelancers.length > 0 ? (
                        <>
                            <div className={`grid grid-cols-1 md:grid-cols-2 ${viewColumns === 3 ? 'lg:grid-cols-3' : viewColumns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-4 sm:gap-6 mb-6 sm:mb-8`}>
                                {currentFreelancers.map((user) => (
                                    <div key={user.id || user.fp_id} className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col h-full">
                                        <div className="p-4 sm:p-6 flex flex-col flex-1">
                                            {/* Header */}
                                            <div className="flex items-start justify-between mb-3 sm:mb-4">
                                                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                                    <div className="relative flex-shrink-0">
                                                        <img src={getProfileImage(user.fp_img || user.image, user.updated_at || user.id || user.fp_id)} alt={user.fp_display_name || user.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-100" />
                                                        {isVerified(user) && (<div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#3a599c] rounded-full flex items-center justify-center"><div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full"></div></div>)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-1 sm:gap-2 flex-wrap">
                                                            <span className="truncate">{capitalizeEachWord(user.fp_display_name || user.name)}</span>
                                                            {(user.is_digilocker === "1" || user.is_digilocker === 1) && (
                                                                <span 
                                                                    title="DigiLocker Verified" 
                                                                    className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded border border-blue-200 flex items-center font-semibold whitespace-nowrap flex-shrink-0"
                                                                >
                                                                    <img src="/images/Digilocer-icon.png" alt="DigiLocker" className="h-3 w-auto mr-1 object-contain mix-blend-multiply" onError={(e) => { e.target.style.display = 'none'; }} />
                                                                </span>
                                                            )}
                                                            {isVerified(user) && <span className="text-[#3a599c] text-sm flex-shrink-0">✓</span>}
                                                            {(() => {
                                                                const badge = getBadge(user.profile_percentage);
                                                                return badge ? (
                                                                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full border ${badge.color} font-medium flex-shrink-0`}>
                                                                        {badge.name}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                        </h3>
                                                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate">{capitalizeEachWord(user.fp_occupation || user.occupation || "Freelancer")}</p>
                                                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-1">
                                                            {(user.fp_ex_year || user.experience) && (user.fp_ex_year || user.experience) !== "0" && <span>{user.fp_ex_year || user.experience}(s)</span>}
                                                            {(user.fp_available || user.availability) && (<span className="text-green-600 font-semibold border border-green-300 px-1.5 sm:px-2 py-0.5 rounded-full bg-green-50 text-[10px] sm:text-xs whitespace-nowrap">• {user.fp_available || user.availability}</span>)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Location and Rating */}
                                            {viewColumns < 4 && (
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600">
                                                    <div className="flex items-center gap-1"><MapPin size={14} className="sm:w-4 sm:h-4 flex-shrink-0" /><span className="truncate">{user.location || getLocationString(user)}</span></div>
                                                    {(Number(user.total_reviews || user.reviews) || 0) > 0 && (
                                                        <div className="flex items-center gap-1 flex-wrap">
                                                            <Star size={14} className="sm:w-4 sm:h-4 text-yellow-400 flex-shrink-0" />
                                                            <span className="text-gray-800 font-medium whitespace-nowrap">{user.average_rating || user.rating}</span>
                                                            <span className="text-xs sm:text-sm">({user.total_reviews || user.reviews} {Number(user.total_reviews || user.reviews) === 1 ? 'review' : 'reviews'})</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Headline */}
                                            {viewColumns === 2 && (user.fp_headline || user.headline) && (user.fp_headline || user.headline) !== "0" && (
                                                <div className="mb-3"><p className="text-xs sm:text-sm text-gray-700 line-clamp-2 leading-relaxed capitalize">{user.fp_headline || user.headline}</p></div>
                                            )}

                                            {/* Languages */}
                                            {viewColumns === 2 && (
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                                                    {getLanguages(user).slice(0, 4).map((lang, idx) => (<span key={idx} className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full">{capitalizeEachWord(lang)}</span>))}
                                                    {(user.fp_profile_tag_line) && user.fp_profile_tag_line !== "0" && (<span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full truncate max-w-full">{capitalizeEachWord(user.fp_profile_tag_line)}</span>)}
                                                </div>
                                            )}

                                            {/* Projects and Rate */}
                                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                                {/* <div className="text-sm text-gray-600">
                                                    <span className="font-semibold text-gray-800">{user.projects || 0}</span> Projects
                                                </div> */}
                                                <div className="text-right ml-auto">
                                                    <div className="text-xl sm:text-2xl font-bold text-gray-800 flex items-baseline justify-end gap-1">
                                                        <span>{formatPrice(Number(user.hourlyRate || user.fp_amt_hour) || 0)}</span>
                                                        <span className="text-xs sm:text-sm font-normal text-gray-600">/{user.amountFor || user.fp_amount_for || "Hour"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-col gap-2 sm:gap-3 mt-auto">
                                                <button onClick={() => router.push(`/freelancer-profile/${encodeURIComponent(user.slug || user.us_slug || user.fp_slug || user.fp_id || user.id)}`)} className="w-full py-2.5 sm:py-3 border-2 border-[#3a599c] text-[#3a599c] hover:bg-[#3a599c] hover:text-white transition-colors rounded-lg font-medium text-sm sm:text-base touch-manipulation" style={{ borderColor: primaryColor }}>View Profile</button>

                                                {!accountDeactivated && viewColumns < 4 && (
                                                    <div className="flex flex-col sm:flex-row gap-2">
                                                        {!isOwnProfile(user) && (<button onClick={() => handleSendMessage(user)} className="flex-1 py-2.5 sm:py-3 text-center bg-white border-2 border-[#3a599c]/30 text-[#3a599c] rounded-lg hover:bg-[#3a599c]/5 hover:border-[#3a599c]/50 transition-colors font-medium flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm touch-manipulation"><MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /><span>Send Message</span></button>)}
                                                        {isCustomer && !isOwnProfile(user) && (<button onClick={() => handleRequestContactDetails(user.id || user.fp_id, user.fp_display_name || user.name)} disabled={requestingContact[user.id || user.fp_id]} className="flex-1 py-2.5 sm:py-3 text-center bg-[#3a599c] text-white rounded-lg hover:bg-[#2d477a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm touch-manipulation"><Phone className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${requestingContact[user.id || user.fp_id] ? 'animate-pulse' : ''}`} /><span className="truncate">{requestingContact[user.id || user.fp_id] ? 'Requesting...' : 'Get Contact'}</span></button>)}
                                                        {isOwnProfile(user) && (<div className="flex-1 py-2.5 sm:py-3 text-center text-gray-500 text-xs sm:text-sm">You cannot send a message to yourself</div>)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="flex justify-center px-2 sm:px-0">
                                <nav className="flex items-center gap-0.5 sm:gap-1">
                                    <button onClick={() => paginate(1)} disabled={currentPage === 1} className="p-1.5 sm:p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation" aria-label="First page"><ChevronsLeft size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                    <button onClick={prevPage} disabled={currentPage === 1} className="p-1.5 sm:p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation" aria-label="Previous page"><ChevronLeft size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                                        const range = isMobile ? 1 : 2;
                                        const showInitial = isMobile ? 1 : 10;
                                        const isWithinRange = number >= currentPage - range && number <= currentPage + range;
                                        const isInitial = number <= showInitial;
                                        const isLast = number === totalPages;
                                        if (isInitial || isLast || isWithinRange) {
                                            return (<button key={number} onClick={() => paginate(number)} className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md text-sm sm:text-base touch-manipulation flex-shrink-0 ${currentPage === number ? "bg-[#3a599c] text-white" : "text-gray-700 hover:bg-gray-100"}`} style={currentPage === number ? { backgroundColor: primaryColor } : {}}>{number}</button>);
                                        }
                                        const prevNumber = number - 1;
                                        const prevWasShown = (prevNumber <= showInitial) || (prevNumber === totalPages) || (prevNumber >= currentPage - range && prevNumber <= currentPage + range);
                                        if (prevWasShown) return <span key={`dots-${number}`} className="px-1 text-gray-400 self-center">...</span>;
                                        return null;
                                    })}
                                    <button onClick={nextPage} disabled={currentPage === totalPages} className="p-1.5 sm:p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation" aria-label="Next page"><ChevronRight size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                    <button onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} className="p-1.5 sm:p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation" aria-label="Last page"><ChevronsRight size={16} className="sm:w-[18px] sm:h-[18px]" /></button>
                                </nav>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-xl shadow-md flex flex-col items-center px-4">
                            <div className="w-40 h-40 sm:w-52 sm:h-52 mb-4">
                                <Lottie animationData={userSearchingAnimation?.default || userSearchingAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">We are currently onboarding freelancers for this service.</h3>
                            <p className="text-gray-600 mb-4 max-w-md">Please check back soon to discover the right professional for your needs.</p>
                        </div>
                    )}
                </main>
            </div>

            {/* Mobile Filter Modal */}
            {showFilterModal && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end">
                    <div className="bg-white rounded-t-xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
                        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Filters</h3>
                            <button
                                onClick={toggleFilterModal}
                                className="text-gray-500 hover:text-gray-700 p-1"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-4 space-y-5">
                            {/* Mobile State Filter - Searchable Dropdown */}
                            <div>
                                <h3 className="font-semibold mb-2 text-gray-800 text-sm">State</h3>
                                <div className="relative" ref={stateDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStateDropdownOpen(!stateDropdownOpen);
                                            setCityDropdownOpen(false);
                                        }}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all text-left flex items-center justify-between"
                                    >
                                        <span className={selectedState ? "text-gray-900" : "text-gray-500"}>
                                            {selectedState
                                                ? statesList.find(s => String(s.s_id) === String(selectedState))?.s_name || "Select State"
                                                : "All States"}
                                        </span>
                                        <ChevronDown className={`text-gray-400 transition-transform ${stateDropdownOpen ? 'transform rotate-180' : ''}`} size={14} />
                                    </button>
                                    {stateDropdownOpen && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                                            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                                                <input
                                                    type="text"
                                                    placeholder="Search states..."
                                                    value={stateSearchTerm}
                                                    onChange={(e) => setStateSearchTerm(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs sm:text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedState("");
                                                        setStateDropdownOpen(false);
                                                        setStateSearchTerm("");
                                                    }}
                                                    className={`w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-gray-50 ${!selectedState ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'}`}
                                                >
                                                    All States
                                                </button>
                                                {statesList
                                                    .filter(state =>
                                                        state.s_name.toLowerCase().includes(stateSearchTerm.toLowerCase())
                                                    )
                                                    .map((state) => (
                                                        <button
                                                            key={state.s_id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedState(String(state.s_id));
                                                                setStateDropdownOpen(false);
                                                                setStateSearchTerm("");
                                                            }}
                                                            className={`w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-gray-50 ${String(selectedState) === String(state.s_id) ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'
                                                                }`}
                                                        >
                                                            {state.s_name}
                                                        </button>
                                                    ))}
                                                {statesList.filter(state =>
                                                    state.s_name.toLowerCase().includes(stateSearchTerm.toLowerCase())
                                                ).length === 0 && (
                                                        <div className="px-4 py-2 text-xs sm:text-sm text-gray-500 text-center">No states found</div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Mobile City Filter - Searchable Dropdown */}
                            <div>
                                <h3 className="font-semibold mb-2 text-gray-800 text-sm">City</h3>
                                <div className="relative" ref={cityDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (selectedState) {
                                                setCityDropdownOpen(!cityDropdownOpen);
                                                setStateDropdownOpen(false);
                                            }
                                        }}
                                        disabled={!selectedState}
                                        className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs sm:text-sm bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] transition-all text-left flex items-center justify-between ${!selectedState ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'text-gray-700'
                                            }`}
                                    >
                                        <span className={selectedCity ? "text-gray-900" : "text-gray-500"}>
                                            {!selectedState
                                                ? "Select State First"
                                                : selectedCity
                                                    ? filteredCities.find(c => String(c.cit_id) === String(selectedCity))?.cit_name || "Select City"
                                                    : "All Cities"}
                                        </span>
                                        <ChevronDown className={`text-gray-400 transition-transform ${cityDropdownOpen ? 'transform rotate-180' : ''}`} size={14} />
                                    </button>
                                    {cityDropdownOpen && selectedState && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                                            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                                                <input
                                                    type="text"
                                                    placeholder="Search cities..."
                                                    value={citySearchTerm}
                                                    onChange={(e) => setCitySearchTerm(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs sm:text-sm focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedCity("");
                                                        setCityDropdownOpen(false);
                                                        setCitySearchTerm("");
                                                    }}
                                                    className={`w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-gray-50 ${!selectedCity ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'}`}
                                                >
                                                    All Cities
                                                </button>
                                                {filteredCities
                                                    .filter(city =>
                                                        city.cit_name.toLowerCase().includes(citySearchTerm.toLowerCase())
                                                    )
                                                    .map((city) => (
                                                        <button
                                                            key={city.cit_id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedCity(String(city.cit_id));
                                                                setCityDropdownOpen(false);
                                                                setCitySearchTerm("");
                                                            }}
                                                            className={`w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-gray-50 ${String(selectedCity) === String(city.cit_id) ? 'bg-gray-50 text-[#3a599c]' : 'text-gray-700'
                                                                }`}
                                                        >
                                                            {city.cit_name}
                                                        </button>
                                                    ))}
                                                {filteredCities.filter(city =>
                                                    city.cit_name.toLowerCase().includes(citySearchTerm.toLowerCase())
                                                ).length === 0 && (
                                                        <div className="px-4 py-2 text-xs sm:text-sm text-gray-500 text-center">No cities found</div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Mobile Salary Filter */}
                            <div>
                                <h3 className="font-semibold mb-2 text-gray-800 text-sm">Filter by Budget</h3>
                                <div className="flex flex-col mt-3 gap-3">
                                    <div className="relative flex-1">
                                        <span className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-xs sm:text-sm">₹</span>
                                        <input
                                            type="number"
                                            value={salaryRange.min === "" ? "" : salaryRange.min}
                                            onChange={(e) => handleSalaryChange(e, "min")}
                                            min="0"
                                            max={typeof salaryRange.max === "number" ? salaryRange.max : undefined}
                                            step="1"
                                            placeholder="0"
                                            className="w-full pl-6 sm:pl-7 pr-9 sm:pr-10 py-2 border border-gray-200 rounded-lg text-xs sm:text-sm"
                                        />
                                        <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">min</span>
                                    </div>
                                    <div className="relative flex-1">
                                        <span className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-xs sm:text-sm">₹</span>
                                        <input
                                            type="number"
                                            value={salaryRange.max === "" ? "" : salaryRange.max}
                                            onChange={(e) => handleSalaryChange(e, "max")}
                                            min={typeof salaryRange.min === "number" ? salaryRange.min : 0}
                                            step="1"
                                            placeholder="100000"
                                            className="w-full pl-6 sm:pl-7 pr-9 sm:pr-10 py-2 border border-gray-200 rounded-lg text-xs sm:text-sm"
                                        />
                                        <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">max</span>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Experience */}
                            <div>
                                <h3 className="font-semibold mb-2 text-gray-800 text-sm">Experience</h3>
                                <div className="relative">
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 appearance-none bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                        value={experience}
                                        onChange={(e) => setExperience(e.target.value)}
                                    >
                                        <option value="">Select Experience</option>
                                        <option value="0-To 1-Year">0 To 1 Year</option>
                                        <option value="2-To-5-Year">2 To 5 Year</option>
                                        <option value="5-To-10-Year">5 To 10 Year</option>
                                        <option value="10-Year">More Than 10 Year</option>
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                                </div>
                            </div>

                            {/* Mobile Remote Work Filter */}
                            <div>
                                <h3 className="font-semibold mb-2 text-gray-800 text-sm">Work Type</h3>
                                <div className="relative">
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 appearance-none bg-white focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c]"
                                        value={remoteType}
                                        onChange={(e) => setRemoteType(e.target.value)}
                                    >
                                        <option value="">All Work Types</option>
                                        <option value="Remote">Remote</option>
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-white pt-4 pb-2 space-y-2 border-t border-gray-100 -mx-4 px-4">
                                <button
                                    onClick={applyFilters}
                                    className="w-full py-3 text-white rounded-lg transition font-medium text-sm hover:opacity-90"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    Apply Filters
                                </button>
                                <button
                                    onClick={() => {
                                        setSalaryRange({ min: "", max: "" });
                                        setExperience("");
                                        setRemoteType("");
                                        setSelectedState("");
                                        setSelectedCity("");
                                        setStateDropdownOpen(false);
                                        setCityDropdownOpen(false);
                                        setStateSearchTerm("");
                                        setCitySearchTerm("");
                                        setCurrentPage(1);
                                        updateUrlWithPage(1);
                                    }}
                                    className="w-full py-2.5 text-gray-600 border border-gray-300 rounded-lg transition font-medium hover:bg-gray-50 text-sm"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Scroll to Top Button */}
            <button
                onClick={scrollToTop}
                className="scroll-to-top-btn fixed bottom-8 right-8 w-12 h-12 bg-[#3a599c] text-white rounded-full shadow-lg flex items-center justify-center opacity-0 transition-opacity duration-300 hover:bg-[#2d477a]"
                style={{ backgroundColor: primaryColor }}
                aria-label="Scroll to top"
            >
                <ChevronUp size={24} />
            </button>

            {/* Modals */}
            {showLoginModal && (
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onLoginSuccess={handleLoginSuccess}
                />
            )}
            {showMessageModal && selectedFreelancer && (
                <MessageModal
                    isOpen={showMessageModal}
                    onClose={() => { setShowMessageModal(false); setSelectedFreelancer(null); }}
                    freelancerId={selectedFreelancer.id || selectedFreelancer.fp_id || selectedFreelancer.fp_u_id}
                    freelancerName={selectedFreelancer.fp_display_name || selectedFreelancer.name}
                />
            )}
            {showContactSuccessModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full shadow-2xl text-center relative">
                        <button 
                          onClick={() => setShowContactSuccessModal(false)} 
                          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="Close modal"
                        >
                          <X size={24} />
                        </button>
                        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Contact Request Sent!</h3>
                        <p className="text-gray-600 mb-6">Your request has been sent successfully. You'll be redirected to your messages.</p>
                        <button onClick={handleContactSuccessClose} className="w-full py-3 bg-[#3a599c] text-white rounded-lg hover:bg-[#2d477a] transition-colors font-medium">Go to Messages</button>
                    </div>
                </div>
            )}
        </div>
    );
};



const FreelancerGridPage = () => (
    <Suspense fallback={<Loader />}>
        <FreelancersPage />
    </Suspense>
);

export default FreelancerGridPage;